import {PageHeader} from '../ui/PageHeader';
import { useCallback, useEffect, useState } from 'react';
import { api } from '../api';
import { Empty, ErrorState, Loading } from '../ui/State';

type Summary = { sources: number; buildings: number; needsReview: number; pendingDocuments: number; unresolvedPeople: number };
type Source = { id: string; source_kind: string; display_name: string; source_address: string; development_name?: string; permission_status: string; sync_status: string };
type Gap = { code: string; label: string; severity: string };
type State = Record<string, number | string | null>;
type Reconciliation = { id: string; development_name: string; status: string; expected_state: State; observed_state: State; gaps: Gap[] };
type Run = { run_number: string; status: string; completed_at?: string };
type Dashboard = { summary: Summary; sources: Source[]; latestRun: Run | null; reconciliations: Reconciliation[] };

const command = (reason: string) => ({ reason, idempotencyKey: crypto.randomUUID() });

export function PortfolioOnboarding() {
  const [data, setData] = useState<Dashboard>();
  const [error, setError] = useState<unknown>();
  const [busy, setBusy] = useState<'discover' | 'check'>();
  const [notice, setNotice] = useState('');
  const load = useCallback(async () => {
    try { setError(undefined); setData(await api<Dashboard>('/api/portfolio-onboarding')); }
    catch (caught) { setError(caught); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  async function run(action: 'discover' | 'check') {
    try {
      setBusy(action); setError(undefined); setNotice('');
      await api(action === 'discover' ? '/api/portfolio-onboarding/sources/discover' : '/api/portfolio-onboarding/runs', {
        method: 'POST', body: JSON.stringify(command(action === 'discover' ? 'Refresh approved Microsoft 365 onboarding sources' : 'Check portfolio onboarding completeness')),
      });
      setNotice(action === 'discover' ? 'Approved Microsoft 365 sources refreshed.' : 'Portfolio check completed.');
      await load();
    } catch (caught) { setError(caught); }
    finally { setBusy(undefined); }
  }

  if (!data && !error) return <Loading />;
  return <>
    <PageHeader title="Portfolio onboarding" context="Track operational control separately from handover completeness. Buildings can be operationally live while unknown, provisional or conflicting information remains in the onboarding queue."
     primaryAction={{kind:'button',label:busy==='check'?'Checking portfolio…':'Check portfolio',disabled:Boolean(busy),onClick:()=>void run('check')}}
     secondaryActions={[{kind:'button',label:busy==='discover'?'Finding sources…':'Find approved sources',disabled:Boolean(busy),onClick:()=>void run('discover')}]}/>
    {error && <ErrorState error={error} />}
    {notice && <div className="notice success">{notice}</div>}
    {data && <>
      <div className="metric-grid onboarding-metrics">
        <div className="metric"><span>Connected sources</span><strong>{data.summary.sources}</strong></div>
        <div className="metric"><span>Buildings</span><strong>{data.summary.buildings}</strong></div>
        <div className="metric"><span>Buildings needing attention</span><strong>{data.summary.needsReview}</strong></div>
        <div className="metric"><span>Documents to review</span><strong>{data.summary.pendingDocuments}</strong></div>
        <div className="metric"><span>People to confirm</span><strong>{data.summary.unresolvedPeople}</strong></div>
      </div>

      <section className="panel">
        <div className="section-heading"><div><h2>Connected sources</h2><p>Only approved mailbox, SharePoint and OneDrive connections are included.</p></div></div>
        {data.sources.length === 0 ? <Empty title="No approved sources found" body="Use Find approved sources after the Microsoft 365 connections have been authorised." /> :
          <div className="onboarding-source-list">{data.sources.map(source => <div className="onboarding-source-row" key={source.id}>
            <div><strong>{source.display_name}</strong><span>{source.source_address}</span></div>
            <span>{source.source_kind}</span><span>{source.development_name ?? 'Building not linked'}</span>
            <span className={`status-badge ${source.permission_status === 'Verified' ? 'success' : 'warning'}`}>{source.permission_status === 'Verified' ? 'Authorised' : 'Check access'}</span>
            <span>{source.sync_status}</span>
          </div>)}</div>}
      </section>

      <section className="panel">
        <div className="section-heading"><div><h2>Building readiness</h2><p>{data.latestRun ? `${data.latestRun.run_number} · ${data.latestRun.status}` : 'Run a portfolio check to identify operational gates and progressive onboarding gaps.'}</p></div></div>
        {!data.latestRun ? <Empty title="No portfolio check yet" body="Choose Check portfolio above to identify missing or ambiguous information." /> :
          <div className="onboarding-building-list">{data.reconciliations.map(row => {
            const onboardingStatus=String(row.observed_state.onboardingStatus??'Not started');
            const operationallyLive=onboardingStatus==='Live';
            const dataGaps=Number(row.observed_state.onboardingDataGaps??0);
            return <article className="onboarding-building-row" key={row.id}>
              <div className="onboarding-building-title"><strong>{row.development_name}</strong><span className={`status-badge ${operationallyLive?'success':'warning'}`}>{operationallyLive?'Operationally live':onboardingStatus}</span><span className={`status-badge ${row.status==='Complete'?'success':'warning'}`}>{row.status==='Complete'?'Onboarding clear':row.status==='Blocked'?'Operational gate':'Onboarding attention'}</span></div>
              <div className="onboarding-counts">
                <span><b>{Number(row.observed_state.residentialUnits??0)}</b> / {Number(row.expected_state.residentialUnits??0)} homes</span>
                <span><b>{Number(row.observed_state.commercialUnits??0)}</b> / {Number(row.expected_state.commercialUnits??0)} commercial</span>
                <span><b>{Number(row.observed_state.documents??0)}</b> documents</span>
                <span><b>{Number(row.observed_state.communications??0)}</b> communications</span>
                <span><b>{Number(row.observed_state.sources??0)}</b> sources</span>
                <span><b>{dataGaps}</b> onboarding gaps</span>
              </div>
              {row.gaps.length > 0 && <ul className="onboarding-gaps">{row.gaps.map(gap => <li key={gap.code}>{gap.label}</li>)}</ul>}
            </article>;
          })}</div>}
      </section>
    </>}
  </>;
}
